<template>
  <div class="odd:pr-5">
    <div
      class="flex justify-between space-x-4 rounded-sm p-2"
      :class="{ 'hover:bg-grey-hover': type === 'phone' }"
    >
      <div class="flex items-center justify-start space-x-4">
        <icon-base
          :icon="iconDetails.icon"
          width="16"
          height="16"
          :view-box="iconDetails.viewBox"
          :class="iconDetails.class"
        />
        <div>
          <info-span-copy
            :text="valueAsString"
            :disabled="!clickToCopy"
            :data-cy="id"
          ></info-span-copy>
        </div>
        <template v-if="type === 'phone' && value && value !== '-'">
          <contact-phone-actions
            v-if="!isBadNumberOrDnC"
            :phone-number="valueAsString"
          />
          <block-phone-actions
            class="mt-2-5"
            :telephone-info="telephoneStatusInfo"
            :contact-name="phoneContactName"
            :is-business="isBusiness"
          />
        </template>
        <div
          v-if="is_primary"
          class="pointer-events-none h-5 w-5 rounded-full bg-primary-background-active text-center text-xs leading-5 font-semibold text-primary"
        >
          P
        </div>
      </div>
      <div>
        <tooltip
          v-if="!loadingSources && sourcesForField"
          ref="sourcesTooltip"
          placement="right"
          :arrow="false"
          sticky="reference"
          theme="none"
          interactive
          :popper-options="popperOptions"
        >
          <icon-base
            :icon="IconSourceDetails"
            data-cy="sources-tooltip"
            class="cursor-pointer"
          />
          <template #content>
            <field-sources-component :sources="sourcesForField.sources" />
          </template>
        </tooltip>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { type Component, computed, ref } from "vue";
import { useStore } from "vuex";

import { TelephoneStatus } from "@/enums/communicationLogs";
import useApplicationsStore from "@/stores/applications";

import ContactPhoneActions from "@/components/communication/ContactPhoneActions.vue";
import IconAddress from "@/components/icons/IconAddress.vue";
import IconBadNumber from "@/components/icons/IconBadNumber.vue";
import IconDoNotContact from "@/components/icons/IconDoNotContact.vue";
import IconSourceDetails from "@/components/icons/IconSourceDetails.vue";
import IconTelephone from "@/components/icons/IconTelephone.vue";
import BlockPhoneActions from "@/views/deals/components/BlockPhoneActions.vue";
import InfoSpanCopy from "@/views/deals/components/InfoSpanCopy.vue";
import FieldSourcesComponent from "@/views/deals/components/fieldSources/FieldSources.vue";

import type { FieldSource } from "@/models/applications";
import type { IRootState } from "@/models/state";
import type { TippyComponent } from "vue-tippy";

type FieldData = {
  id: string;
  value: string;
  is_primary?: boolean;
  line_type?: string;
};

type IconDetails = {
  icon: Component;
  class?: string;
  viewBox?: string;
};

const {
  type = "address",
  data,
  isBusiness,
} = defineProps<{
  type?: "address" | "phone";
  clickToCopy?: boolean;
  data: FieldData;
  isBusiness?: boolean;
  loadingSources?: boolean;
}>();

const { id, value, is_primary } = data;

const { getters } = useStore<IRootState>();
const applicationsStore = useApplicationsStore();

const fieldSources = computed<Record<string, FieldSource[]>>(
  () => getters["applications/field_sources"]
);

const sourcesTooltip = ref<TippyComponent | null>(null);
const popperOptions = ref<TippyComponent["popperOptions"]>({
  modifiers: [
    {
      name: "flip",
      options: { fallbackPlacements: ["bottom", "top"] },
    },
  ],
});

const telephoneStatusInfo = computed(() => {
  const phoneNumbers = applicationsStore.phoneNumbers;
  if (!phoneNumbers) {
    return null;
  }
  const telephones = isBusiness
    ? phoneNumbers.business?.all_telephones
    : phoneNumbers.owners?.flatMap(({ all_telephones }) => all_telephones);
  return (
    telephones?.find((phone) => phone.number === value?.toString()) ?? null
  );
});

const isBadNumberOrDnC = computed(() =>
  [TelephoneStatus.BadNumber, TelephoneStatus.DoNotContact].some(
    (status) => status === telephoneStatusInfo.value?.status
  )
);

const valueAsString = computed(() =>
  Array.isArray(value) ? value.join(", ") : value
);

const phoneContactName = computed(() => {
  if (isBusiness) {
    return applicationsStore.phoneNumbers?.business?.business_legal_name || "";
  }

  const phoneStr = value?.toString();
  const ownerFromStore = applicationsStore.phoneNumbers?.owners?.find((owner) =>
    owner.all_telephones?.some((phone) => phone.number === phoneStr)
  );

  return ownerFromStore?.full_name || "";
});

const iconDetails = computed<IconDetails>(() => {
  if (type === "address") {
    return { icon: IconAddress };
  }
  const options = {
    [TelephoneStatus.DoNotContact]: {
      icon: IconDoNotContact,
      class: "text-error",
    },
    [TelephoneStatus.BadNumber]: {
      icon: IconBadNumber,
      class: "text-warning",
      viewBox: "1 2 10 10",
    },
  };
  return telephoneStatusInfo.value?.status
    ? options[telephoneStatusInfo.value?.status]
    : { icon: IconTelephone };
});

const sourcesForField = computed(() => {
  const sourceKey = type === "phone" ? "phone_number" : undefined;
  if (!sourceKey || !Array.isArray(fieldSources.value?.[sourceKey])) {
    return undefined;
  }
  return fieldSources.value?.[sourceKey]?.find((source) => {
    return source.value === data.value;
  });
});
</script>
